import {
  FeatureName,
  Issue,
  Message,
  useAddIsueMessageReaction,
  useCurrentAccount,
  useDeleteIsueMessageReaction,
  useGetUsersIsueMessageReaction,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import * as Haptics from 'expo-haptics';
import { useMemo, useState } from 'react';
import { FlatList, Platform, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import { EmojiType } from 'rn-emoji-keyboard';
import { useOpenedState } from '../../../hooks';
import { Colors } from '../../../themes/colors';
import { SingleEmoji } from '../../emoji/singleEmoji';
import { ReactionMap, UserInfoModal, UserInfoReaction } from './types';
import { UserReactionsView } from './userReactionsView';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

type ReactionlistProps = {
  onOpenNewReaction: () => void;
  issue: Issue;
  message: Message;
};

export const ReactionList = (props: ReactionlistProps) => {
  const { onOpenNewReaction } = props;
  const isUserReactionMessageEnabled = useIsFeatureEnabled(
    FeatureName.IssueMessageReaction
  );
  const isWeb = Platform.OS === 'web';
  const { mutateAsync: DeleteReactionIssue, isPending: isPendingDeleteEmoji } =
    useDeleteIsueMessageReaction();
  const { data: usersReaction } = useGetUsersIsueMessageReaction(
    props?.message?.reactions
  );
  const { isOpened, onOpen, onClose } = useOpenedState();
  const { account } = useCurrentAccount();
  const { mutateAsync: AddReaction, isPending: isPendingNewEmoji } =
    useAddIsueMessageReaction();

  const [usersReactions, setUsersReactions] = useState<UserInfoModal>({
    users: [],
    reactionType: '',
  });

  const reactionCounts = useMemo(() => {
    const users = usersReaction?.account?.listReactionUsers?.items ?? [];

    return Object.values(
      (props.message?.reactions || []).reduce<Record<string, ReactionMap>>(
        (acc, reaction) => {
          if (!acc[reaction.reactionType]) {
            acc[reaction.reactionType] = {
              reaction,
              count: 0,
              users: [],
            };
          }
          const user = users.find(
            (user: UserInfoReaction) => user.id === reaction.createdBy
          );

          if (user) {
            acc[reaction.reactionType].users.push({
              ...user,
              reactionId: reaction.id,
            });
          }
          acc[reaction.reactionType].count += 1;
          return acc;
        },
        {}
      )
    );
  }, [props.message.reactions, usersReaction]);

  if (
    !props?.message?.reactions?.length ||
    !isUserReactionMessageEnabled ||
    isWeb
  )
    return null;

  const onDeleteReaction = async (itemReaction: ReactionMap) => {
    const user = itemReaction.users.find(
      (user: UserInfoReaction) => user.id === account?.id
    );

    if (!user) {
      addNewEmoji(itemReaction.reaction.reactionType);
      return;
    }

    if (isPendingDeleteEmoji) return;

    await DeleteReactionIssue({
      input: {
        issueMessageId: props.message.id,
        reactionId: user.reactionId,
      },
    });
  };

  const addNewEmoji = async (emoji: EmojiType['emoji']) => {
    if (isPendingNewEmoji) return;

    await AddReaction({
      input: {
        issueMessageId: props.message.id,
        reactionType: emoji,
      },
    });
  };

  const activeEmoji = (itemReaction: ReactionMap) =>
    itemReaction.users.some(
      (user: UserInfoReaction) => user.id === account?.id
    );

  return (
    <View style={styles.reactionListContainer}>
      <FlatList
        data={reactionCounts}
        horizontal
        keyExtractor={(item, index) => `reaction-${item.reaction.id}-${index}`}
        renderItem={({ item }) => (
          <View key={item.reaction.id}>
            <TouchableOpacity
              style={[
                styles.singleEmoji,
                activeEmoji(item) && styles.activeEmoji,
              ]}
              activeOpacity={1}
              onPress={() => onDeleteReaction(item)}
              onLongPress={() => {
                setUsersReactions({
                  users: item.users,
                  reactionType: item.reaction.reactionType,
                });
                onOpen();
              }}
            >
              <SingleEmoji
                emojiStyle={styles.emojiStyle}
                emoji={item.reaction.reactionType}
                onPress={() => onDeleteReaction(item)}
                onLongPress={() => {
                  Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
                  setUsersReactions({
                    users: item.users,
                    reactionType: item.reaction.reactionType,
                  });
                  onOpen();
                }}
              />
              <Text style={styles.countLabel}>{item.count}</Text>
            </TouchableOpacity>
          </View>
        )}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.flatListContent}
      />
      <UserReactionsView
        isOpened={isOpened}
        usersReactions={usersReactions}
        onUsersInfoClose={() => {
          setUsersReactions({
            users: [],
            reactionType: '',
          });
          onClose();
        }}
      />
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  reactionListContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    columnGap: 8,
    zIndex: 9,
    marginTop: 6,
  },
  flatListContent: {
    columnGap: 8,
  },
  singleEmoji: {
    flexDirection: 'row',
    borderRadius: 14,
    backgroundColor: role.surfaceHover,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 5,
    zIndex: 9,
    borderWidth: 1.5,
    borderColor: role.borderSubtle,
  },
  activeEmoji: {
    backgroundColor: Colors.orangeActive,
    borderWidth: 1.5,
    borderColor: Colors.orangeActiveHovered,
  },
  emojiStyle: {
    fontSize: 14,
    marginRight: 3,
    letterSpacing: -0.25,
  },
  countLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: role.textStrong,
  },
}));
